import * as React from "react";
import { ImageField, List, ReferenceField, useRedirect } from "react-admin";
import {
  Card,
  Typography,
  Button,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Draggable } from "@hello-pangea/dnd";
import { getDaysAgo } from "../utils/DateTimeUtils";
import {
  getPurchaseStatusName,
  getPurchaseTotalItems,
  isDeliveryPurchase,
  isPurchaseDelivered,
  isPurchaseMultipleProducts,
} from "../utils/PurchaseUtils";
import InfoButton from "@mui/icons-material/Info";

const Purchase = ({ purchase, index, onDeliverClick }) => {
  const theme = useTheme();
  const redirect = useRedirect();
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  if (!purchase) return null;

  const handleClick = () => {
    if (!purchase) return;
    redirect("show", "purchases", purchase.id);
  };

  const renderOtherProductsAccordion = () => {
    const purchaseHasMultipleProducts = isPurchaseMultipleProducts(purchase);
    const otherProductsCount = getPurchaseTotalItems(purchase) - 1;

    return (
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <Typography
          variant="subtitle1"
          color="textPrimary"
          style={purchaseHasMultipleProducts ? { textDecoration: "underline", cursor: "pointer" } : {}}
          onClick={purchaseHasMultipleProducts ? handleOpen : () => {}}
        >
          {`${purchase.productsData?.[0]?.name} ${
            purchaseHasMultipleProducts
              ? `y ${otherProductsCount} ${
                  otherProductsCount > 1 ? "productos" : "producto"
                } más`
              : ""
          }`}
        </Typography>
        {purchaseHasMultipleProducts && (
          <IconButton onClick={handleOpen}>
            <InfoButton fontSize="small" />
          </IconButton>
        )}
      </div>
    );
  };

  return (
    <Draggable draggableId={String(purchase?.id)} index={index}>
      {(provided, snapshot) => (
        <div
          style={{ marginBottom: theme.spacing(1) }}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
        >
          <Card
            style={{
              opacity: snapshot.isDragging ? 0.9 : 1,
              transform: snapshot.isDragging ? "rotate(2deg)" : "",
            }}
            elevation={snapshot.isDragging ? 4 : 2}
          >
            <div style={{ padding: theme.spacing(1), display: "flex", flexDirection: "row", alignItems: "center" }}>
              <ReferenceField
                source="product"
                record={purchase}
                reference="products"
                link={false}
              >
                <ImageField
                  source="images[0].url"
                  label="Imagen"
                  sx={{
                    "& img": {
                      maxWidth: 70,
                      maxHeight: 70,
                      objectFit: "contain",
                    },
                  }}
                />
              </ReferenceField>
              <div style={{ display: "flex", flex: 1, flexDirection: "column", alignItems: "flex-start", paddingLeft: theme.spacing(1) }}>
                <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center" }}>
                  <Typography variant="body2" color="textSecondary">
                    {isPurchaseDelivered(purchase) ? "Entregado" : "Comprado"}{" "}
                    {getDaysAgo(
                      isPurchaseDelivered(purchase)
                        ? purchase?.deliveredPurchase?.deliveredDate
                        : purchase?.paidDate,
                      "hace"
                    )}
                  </Typography>
                  {isDeliveryPurchase(purchase) && (
                    <Typography
                      variant="caption"
                      style={{
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.common.white,
                        borderRadius: 4,
                        padding: "2px 6px",
                        fontSize: "0.75rem",
                        marginLeft: 8,
                      }}
                    >
                      Delivery
                    </Typography>
                  )}
                </div>

                {renderOtherProductsAccordion()}

                <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", width: "100%", paddingRight: theme.spacing(1), marginTop: 8 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
                    {getPurchaseStatusName(purchase?.status)}
                  </Typography>
                  <div>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleClick}
                    >
                      Ver detalle
                    </Button>
                    {!isPurchaseDelivered(purchase) &&
                      !isDeliveryPurchase(purchase) && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={onDeliverClick}
                          style={{ marginLeft: 8 }}
                        >
                          Entregar
                        </Button>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
          <Dialog
            onClose={handleClose}
            open={open}
            fullWidth={true}
            maxWidth={"xs"}
            scroll={"paper"}
          >
            <DialogTitle>Pedido</DialogTitle>
            <DialogContent>
              <List pagination={false} exporter={false} actions={false}>
                {purchase.productsData.map((product) => (
                  <ListItem>
                    <ListItemText
                      primary={product.name}
                      secondary={`${product.quantity} ${
                        product.quantity > 1 ? "unidades" : "unidad"
                      }`}
                    />
                  </ListItem>
                ))}
              </List>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Cerrar</Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </Draggable>
  );
};

export default Purchase;
